import { type JSX } from 'react';
import { Switch } from '@viniciomendesr/rayrovisk-design-system';
import { ChipMulti, options } from '../components/chips';

/**
 * `WORDS_USED` and the last two promises are confeitaria and buffet words,
 * kept from the approved mockup by the owner's call on 2026-08-08. Nothing on
 * this screen reads her niche, so they are wrong for a lawyer or a personal
 * trainer and she unmarks them. Marked defaults would reach the prompt; these
 * start clear.
 */
const WORDS_USED = options('encomenda', 'sob medida', 'massa', 'recheio', 'com antecedência');
const WORDS_AVOIDED = options('amores', 'gente', 'bombar', 'insano', 'imperdível', 'link na bio');
const FORBIDDEN_TOPICS = options('política', 'dieta', 'religião', 'saúde');
const FORBIDDEN_CLAIMS = options(
  'entrega em menos de 3 dias',
  'preço fixo por convidado',
  'resultado garantido'
);

export interface VoiceRules {
  wordsUsed: string[];
  wordsAvoided: string[];
  forbiddenTopics: string[];
  forbiddenClaims: string[];
  regulated: boolean;
}

export function VoiceRulesStep({
  rules,
  onChange
}: {
  rules: VoiceRules;
  onChange: (patch: Partial<VoiceRules>) => void;
}): JSX.Element {
  return (
    <>
      <h1 style={{ margin: 0, font: '400 24px/1.2 var(--rvk-font-expressive)' }}>
        O que você diz, e o que você nunca diria
      </h1>
      <p
        style={{
          margin: 0,
          maxWidth: '60ch',
          font: '400 14px/1.6 var(--rvk-font-functional)',
          color: 'var(--rvk-text-secondary)'
        }}
      >
        Marque o que é seu, desmarque o que não é. O que ficar marcado entra como regra em todo
        prompt que você gerar. Dá para mudar depois.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '24px 28px'
        }}
      >
        <ChipMulti
          legend="Palavras que eu uso"
          hint="O prompt escreve com elas."
          add="adicionar palavra"
          choices={WORDS_USED}
          values={rules.wordsUsed}
          onChange={(values) => onChange({ wordsUsed: values })}
        />
        <ChipMulti
          legend="Palavras que eu não uso"
          hint="O prompt não escreve nenhuma delas."
          add="adicionar palavra"
          choices={WORDS_AVOIDED}
          values={rules.wordsAvoided}
          onChange={(values) => onChange({ wordsAvoided: values })}
        />
        <ChipMulti
          legend="Assuntos que eu não trato"
          hint="Nenhum roteiro puxa gancho por aqui."
          add="adicionar assunto"
          choices={FORBIDDEN_TOPICS}
          values={rules.forbiddenTopics}
          onChange={(values) => onChange({ forbiddenTopics: values })}
        />
        <ChipMulti
          legend="Promessas que eu não faço"
          hint="Nem no gancho, nem na chamada final."
          add="adicionar promessa"
          choices={FORBIDDEN_CLAIMS}
          values={rules.forbiddenClaims}
          onChange={(values) => onChange({ forbiddenClaims: values })}
        />
      </div>

      <div style={{ borderTop: '1px solid var(--rvk-border-subtle)', paddingTop: 18 }}>
        <Switch
          label="Meu nicho é regulado"
          description="Saúde, jurídico ou financeiro. O prompt passa a exigir linguagem conservadora."
          checked={rules.regulated}
          onCheckedChange={(checked) => onChange({ regulated: checked })}
        />
      </div>
    </>
  );
}
